import type { AxiosInstance } from "axios";
import { Loader2, Store } from "lucide-react";
import { useEffect, useState } from "react";

import { ClientInvites } from "../components/ClientInvites.js";
import { AgencyJoinCard, ClientRequests } from "../components/ClientRequests.js";
import { Button } from "../components/ui/button.js";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card.js";
import { Input } from "../components/ui/input.js";
import { Label } from "../components/ui/label.js";
import { Select } from "../components/ui/select.js";
import { apiErrorMessage } from "../lib/api.js";

/**
 * Connect ANOTHER store to this workspace — the agency flow.
 *
 * A store is added by installing the Shopify app, the same OAuth as the first store. The
 * difference is that this install carries a SIGNED GRANT proving this workspace owns the
 * connection, so the new store joins THIS org instead of creating a fresh one. The API
 * mints the grant; the gateway verifies it. See `POST /api/v2/stores/connect`.
 */
export function AddStoreScreen({ client }: { readonly client: AxiosInstance }): React.JSX.Element {
  const [shop, setShop] = useState("");
  /**
   * The organizations this member may add a store to. With more than one (someone who manages two
   * agencies, or an agency and their own brand) they must say which — it used to be the first.
   */
  const [orgs, setOrgs] = useState<readonly { orgId: string; name: string }[]>([]);
  const [orgId, setOrgId] = useState("");

  useEffect(() => {
    void client
      .get<{ success: boolean; data: unknown }>("/api/v2/stores/connect/orgs")
      .then((response) => setOrgs(Array.isArray(response.data.data) ? (response.data.data as { orgId: string; name: string }[]) : []))
      .catch((error: unknown) => {
        // Fully handled: without the list the API picks the one org, or says to choose.
        void error;
      });
  }, [client]);
  const mustChoose = orgs.length > 1;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const domain = normaliseShop(shop);
      const response = await client.post<{ success: boolean; data: { installUrl: string } }>("/api/v2/stores/connect", {
        shop: domain,
        ...(mustChoose ? { orgId } : {}),
      });
      // Full-page navigation to Shopify's install/consent — not a fetch.
      window.location.assign(response.data.data.installUrl);
    } catch (caught) {
      setError(apiErrorMessage(caught, "Could not start the connection."));
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-3xl min-w-0 flex-col gap-4">
      <header>
        <h1 className="text-xl font-semibold">Connect a store</h1>
        <p className="text-muted-foreground text-sm">
          Add a Shopify store you manage, or invite a client to connect theirs.
        </p>
      </header>

      <Card className="gap-5">
        <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Store className="text-primary size-5" />
              Connect a store yourself
            </CardTitle>
            <CardDescription>
              A store you can install apps on yourself. You will be sent to Shopify to approve access.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="shop">Store domain</Label>
              <Input
                id="shop"
                required
                placeholder="your-store.myshopify.com"
                value={shop}
                onChange={(e) => setShop(e.target.value)}
              />
              <p className="text-muted-foreground text-xs">
                Your plan may limit how many stores you can add. If you are at the limit, upgrade first.
              </p>
            </div>

            {mustChoose && (
              <div className="grid gap-1.5">
                <Label htmlFor="connect-org">Add it to</Label>
                <Select id="connect-org" required value={orgId} onChange={(e) => setOrgId(e.target.value)}>
                  <option value="" disabled>
                    Choose an organization…
                  </option>
                  {orgs.map((org) => (
                    <option key={org.orgId} value={org.orgId}>
                      {org.name}
                    </option>
                  ))}
                </Select>
              </div>
            )}

            {error !== null && (
              <p className="text-destructive text-sm" role="alert">
                {error}
              </p>
            )}

            <Button type="submit" disabled={busy || (mustChoose && orgId === "")} className="w-fit">
              {busy && <Loader2 className="animate-spin" />}
              Continue to Shopify
            </Button>
          </CardContent>
        </form>
      </Card>

      {/* Agency side — each renders nothing for a brand, or a member who does not manage the agency. */}
      <ClientRequests client={client} />
      <ClientInvites client={client} />
      {/* Merchant side — offered to a brand's owner/admin only. */}
      <AgencyJoinCard client={client} />
    </div>
  );
}

/**
 * Accept what a merchant is likely to type — a bare handle, a full URL, or the domain —
 * and reduce it to `handle.myshopify.com`. The API and the gateway validate it too; this
 * is a courtesy so a pasted URL does not bounce.
 */
export function normaliseShop(input: string): string {
  let value = input.trim().toLowerCase();
  value = value.replace(/^https?:\/\//, "").replace(/\/.*$/, "");
  if (value === "") return value;
  if (!value.includes(".")) return `${value}.myshopify.com`;
  return value;
}
